import { z } from "zod";
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { AdditionalFlow } from "@/domains/auth/constants/additionalFlow";
import { setResetPasswordMethod } from "@/domains/auth/slice";
import useResetTfaGoogleCode from "@/domains/auth/hooks/reset-tfa/useResetTfaGoogleCode";
import { useAuthFlow } from "@/domains/auth/containers/context";
import AuthTitle from "@/domains/auth/components/AuthTitle";
import AuthResetTfaAccountRecovery from "@/domains/auth/components/reset-tfa/AuthResetTfaAccountRecovery";
import AuthResetTfaAuthenticator from "@/domains/auth/components/reset-tfa/method/AuthResetTfaAuthenticator";

const recoveryCodeSchema = z.preprocess(
  (value) => {
    if (typeof value === "string") {
      return value.replace(/\s/g, ""); // Удаляем пробелы
    }
    return value;
  },
  z
    .string()
    .min(16, "Код должен содержать 16 символов")
    .max(16, "Код должен содержать 16 символов")
    .regex(/^[A-Za-z0-9]+$/, "Recovery code должен содержать только буквы и цифры"),
);

export default function AuthResetTfaAuthenticatorContainer() {
  const { openOnly, goBack } = useAuthFlow();
  const dispatch = useAppDispatch();
  const [recoveryCode, setRecoveryCode] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isRecoveryAccount, setIsRecoveryAccount] = useState(false);
  const { checkCodeHandler, checkIsCompleted, checkIsPending, checkError } =
    useResetTfaGoogleCode();

  useEffect(() => {
    if (recoveryCode) {
      try {
        setValidationError("");
        recoveryCodeSchema.parse(recoveryCode);
      } catch (error) {
        if (error instanceof z.ZodError) {
          setValidationError(error.errors[0].message);
        }
      }
    } else {
      setValidationError("");
    }
  }, [recoveryCode]);

  const onSubmit = useCallback(
    async (e: SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (recoveryCode.length === 6 && !checkIsPending && !validationError) {
        checkCodeHandler(recoveryCode);
      }
    },
    [checkCodeHandler, checkIsPending, recoveryCode, validationError],
  );

  const backHandler = useCallback(() => {
    goBack();
    dispatch(setResetPasswordMethod(null));
  }, [dispatch, goBack]);

  useEffect(() => {
    if (recoveryCode.length === 6 && !checkIsPending && !validationError) {
      checkCodeHandler(recoveryCode);
    }
  }, [checkCodeHandler, recoveryCode, checkIsPending, validationError]);

  useEffect(() => {
    if (checkIsCompleted) {
      openOnly(AdditionalFlow.AuthUpdatePassword);
    }
  }, [openOnly, checkIsCompleted]);

  const onResetAccess = useCallback(() => {
    setIsRecoveryAccount(true);
  }, []);

  const backToInputRecoveryCode = useCallback(() => {
    setIsRecoveryAccount(false);
  }, []);

  const disableSubmitButton = useMemo(
    () => !!validationError || !recoveryCode,
    [validationError, recoveryCode],
  );

  const error = useMemo(() => validationError || checkError, [validationError, checkError]);

  if (checkIsCompleted) {
    return null;
  }

  if (isRecoveryAccount) {
    return (
      <>
        <AuthTitle back={backToInputRecoveryCode}>Нет доступа к Authenticator</AuthTitle>
        <AuthResetTfaAccountRecovery
          onSubmit={() => {
            // TODO:
          }}
        />
      </>
    );
  }

  return (
    <>
      <AuthTitle back={backHandler}>Сброс Authenticator</AuthTitle>
      <AuthResetTfaAuthenticator
        error={error}
        code={recoveryCode}
        onCodeChange={setRecoveryCode}
        onSubmit={onSubmit}
        pending={checkIsPending}
        onResetAccess={onResetAccess}
        disableSubmitButton={disableSubmitButton}
      />
    </>
  );
}
